"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OnDemandProgramme = exports.isOnDemandProgrammeConstructorProps = exports.isOnDemandProgramme = void 0;
const tslib_1 = require("tslib");
const core_services_1 = require("@iotv/core-services");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const dayjs_1 = tslib_1.__importDefault(require("dayjs"));
const utc_1 = tslib_1.__importDefault(require("dayjs/plugin/utc"));
const uuid_1 = require("uuid");
const ModelTypes_1 = require("../../../types/ModelTypes");
const ProgrammeTypes_1 = require("../../../types/ProgrammeTypes");
const helpers_1 = require("../../../util/time/helpers");
const ModelCommon_1 = require("../../ModelCommon");
dayjs_1.default.extend(utc_1.default);
const reasonableWindowcount = (0, helpers_1.getReasonableValueNumber)(1, 1000);
const isOnDemandProgramme = (x) => {
    const c = x;
    const isConstructorProps = (0, exports.isOnDemandProgrammeConstructorProps)(x);
    const isInstance = c instanceof OnDemandProgramme;
    const isOnDemandProgramme = isConstructorProps && isInstance;
    if (c && !isOnDemandProgramme) {
        ;
        c?.type === 'OnDemandProgramme' &&
            console.log(`WARN isOnDemandProgramme returning false for OnDemandProgramme ${c.id} with`, { isConstructorProps, isInstance });
    }
    return isOnDemandProgramme;
};
exports.isOnDemandProgramme = isOnDemandProgramme;
const isOnDemandProgrammeConstructorProps = (x) => {
    const { name, supportedType, startTime, windows, windowDuration, windowGapDuration, repeats } = x ?? {};
    const isOnDemandProgrammeConstructorProps = typeof name === 'string' &&
        typeof supportedType === 'string' &&
        (startTime === null || (0, iotv_v3_types_1.isNonNaNNumber)(startTime)) &&
        (windows === null || (0, iotv_v3_types_1.isNonNaNNumber)(windows)) &&
        (windowDuration === null || (0, iotv_v3_types_1.isNonNaNNumber)(windowDuration)) &&
        (windowGapDuration === null || (0, iotv_v3_types_1.isNonNaNNumber)(windowGapDuration)) &&
        (repeats === null || (0, iotv_v3_types_1.isNonNaNNumber)(repeats));
    if (!isOnDemandProgrammeConstructorProps) {
        ;
        x?.type === 'OnDemandProgramme' &&
            console.log(`WARN isOnDemandProgrammeConstructorProps returning false for OnDemandProgramme ${x?.id} with`, {
                name,
                supportedType,
                startTime,
                windows,
                windowDuration,
                windowGapDuration,
                repeats,
            });
    }
    return isOnDemandProgrammeConstructorProps;
};
exports.isOnDemandProgrammeConstructorProps = isOnDemandProgrammeConstructorProps;
class OnDemandProgramme extends ModelCommon_1.ModelCommon {
    constructor({ id = (0, uuid_1.v4)(), supportedType, name, appliedTimestamps, startTime, windows, windowDuration, windowGapDuration, repeats, ...others }) {
        super('OnDemandProgramme', { id, ...others });
        this.updateAppliedTimestamps = (timestampMs) => {
            if (!this.appliedTimestamps)
                this.appliedTimestamps = [];
            this.appliedTimestamps.unshift(timestampMs);
            this.appliedTimestamps = this.appliedTimestamps.slice(0, this.maxAppliedTimestamps);
        };
        /**
         * The total cycle time in minutes
         *
         * Includes:
         * - number of windows
         * - window duration
         * - window gap duration
         * - repeats
         *
         * Exclude the last gap duration, as there is nothing following it
         */
        this.getTotalRepeatTime = () => this.windows * (this.windowDuration + this.windowGapDuration) * this.repeats - this.windowGapDuration;
        /**
         * The calculated `delayMins` to apply to each `rollingProgammeConfig`
         *
         * Based on:
         * - number of windows
         * - window duration
         * - window gap duration
         *
         * The device enters the delay phase first, then the operation phase (`windowDuration`), so every config (including the first) starts with the calculated delay
         */
        this.getCalculatedDelayMins = () => this.getTotalRepeatTime() - this.windowDuration;
        /**
         * Calculates a config for any window index
         */
        this.getOnDemandProgrammeConfigForWindow = (window, realtime = false) => {
            let res = null;
            const { startTime, windows, windowDuration, windowGapDuration, repeats } = this;
            if ((0, core_services_1.between)(window, 0, windows * repeats)) {
                const now = (0, dayjs_1.default)().unix();
                const duration = this.getTotalRepeatTime();
                const disabled = startTime === 0;
                const shouldDisable = startTime * iotv_v3_types_1.Milliseconds.SECOND + duration * iotv_v3_types_1.Milliseconds.MINUTE <= now;
                const disable = disabled || shouldDisable;
                const interCycleTimeMins = windowDuration + windowGapDuration;
                /** offset from '0' time, in minutes */
                const offset = interCycleTimeMins * window;
                const start = (0, dayjs_1.default)(realtime ? startTime * iotv_v3_types_1.Milliseconds.SECOND : 0)
                    .utc()
                    .add(offset, 'minutes');
                const startHour = start.hour();
                const startMinute = start.minute();
                res = {
                    enable: !disable,
                    dayMask: disable ? iotv_v3_types_1.dayMaskTemplate : iotv_v3_types_1.dayMaskAllTrueTemplate,
                    scheduleNumber: 1 + Math.floor(window / windows),
                    moistureThreshold: 0,
                    duration_mins: windowDuration,
                    utc_start_hour: startHour,
                    utc_start_min: startMinute,
                };
            }
            else {
                console.log(`WARN getOnDemandProgrammeConfigForWindow ${window} is out of bounds, max is ${windows * repeats - 1}`);
            }
            return res;
        };
        this.name = name;
        this.supportedType = supportedType;
        this.appliedTimestamps = appliedTimestamps ?? null;
        this.startTime = startTime ?? 0;
        this.windows = reasonableWindowcount(windows) ? (windows ?? 1) : 1;
        this.windowDuration = windowDuration ?? 1;
        this.windowGapDuration = windowGapDuration ?? 1;
        this.repeats = repeats ?? 1;
    }
    getUserAttributes() {
        return {
            ...super.getUserAttributes(),
            supportedType: {
                type: 'Enum',
                editable: true,
                required: true,
                enumKv: ProgrammeTypes_1.ProgrammeSupportedTypesEnum,
            },
            startTime: {
                type: 'Date',
                editable: false,
                required: true,
                precision: ModelTypes_1.UserAttributeKeyDatePrecision.DateTimeMins,
                label: 'Start Time',
                displayFn: (v) => dayjs_1.default
                    .utc(v * iotv_v3_types_1.Milliseconds.SECOND)
                    .local()
                    .valueOf(),
                valueFn: (v) => v / iotv_v3_types_1.Milliseconds.SECOND,
            },
            windows: {
                type: 'Number',
                editable: false,
                required: true,
                precision: 0,
                label: 'Windows',
                default: 1,
                limits: { min: 1 },
            },
            windowDuration: {
                type: 'Number',
                editable: false,
                required: true,
                precision: 0,
                label: 'Duration',
                units: 'minutes',
                default: 1,
                limits: { min: 1 },
            },
            windowGapDuration: {
                type: 'Number',
                editable: false,
                required: true,
                precision: 0,
                label: 'Window Gap',
                units: 'minutes',
                default: 1,
            },
            repeats: {
                type: 'Number',
                editable: false,
                required: true,
                precision: 0,
                label: 'Repeats',
                limits: { min: 1, max: 5 },
            },
        };
    }
}
exports.OnDemandProgramme = OnDemandProgramme;
